// InterventionList:
// - renders a list of summarized interventions that apply to selected risk factors

import {
  component,
  store,
} from 'reefjs'

import {
  debounce,
  getRiskFactorData,
  isValidAssessmentItemId,
  parseInterventions,
  setupLogger,
} from '../utils.js'
import {
  interventionSummaryTemplate,
} from '../templates.js'
import * as interventionData from '/assets/interventions.js'

const { logger, loggerError } = setupLogger('InterventionList', 'orange')


const storeStructure = () => (Object.keys(interventionData.default)).map(k => [k, new Set()])
const storeInit = () => Object.fromEntries(storeStructure())

const interventionStore = store(storeInit(), {
  // interventionStore takes emitted data from selected risk factors and:
  // - deduplicates the risk factors' associated interventions
  //   (using Set objects, because they only allow unique values)
  // - structures the result for rendering
  // - stores associated risk factor ids for each of those interventions (?)

  update(store, riskFactorIds) {
    // logger('updating intervention store with', riskFactorIds)
    const riskFactorData = getRiskFactorData(riskFactorIds)
    const idCollections = riskFactorData.reduce((totalAcc, currentGroup) => {
      // This `reduce` call:
      // - creates an object with keys for every intervention category
      // - collects intervention ids from each risk factor into their
      //   respective categories

      // For any groups that don't have any interventions, just return early.
      if (!currentGroup.interventions) return totalAcc

      Object.entries(currentGroup.interventions).forEach((item) => {
        const category = item[0]
        const interventionIds = parseInterventions(item)
        if (!totalAcc[category]) {
          totalAcc[category] = interventionIds[category]
        } else {
          totalAcc[category] = [ ...totalAcc[category], ...interventionIds[category] ]
        }
      })
      return totalAcc
    }, {})

    // For every intervention category, update the store's state with
    // the current selection:
    for (const category of Object.keys(interventionData)) {
      if (Object.keys(idCollections).includes(category)) {
        this.updateCategory(store, category, idCollections[category].sort((a, b) => a - b))
      } else {
        this.emptyCategory(store, category)
      }
    }
  },

  updateCategory(store, category, newData) {
    const updatedCategory = new Set(newData)
    const outgoing = store[category].difference(updatedCategory)
    const incoming = updatedCategory.difference(store[category])

    for (let oid of outgoing) {
      store[category].delete(oid)
    }
    for (let iid of incoming) {
      store[category].add(iid)
    }
  },

  emptyCategory(store, category) {
    if (store[category]) {
      store[category].clear()
    }
  }
}, 'interventions')



export default class InterventionList extends HTMLElement {
  constructor() {
    super()
    logger(this)

    this.forAssessmentId = this.getAttribute('for-assessment')

    component(this, this.template, {
      signals: ['interventions'],
    })
  }

  connectedCallback() {
    // Retrieve any selections saved to localStorage for an immediate update to the store:
    const cachedSelection = Object.keys(localStorage)
    const validCachedSelection = cachedSelection.filter((c) => isValidAssessmentItemId(c, this.forAssessmentId))
    interventionStore.update(validCachedSelection)

    // Debounce the listener's callback a bit to batch updates:
    document.addEventListener('reef:signal-risk-selection', debounce((event) => {
      logger('heard reef:signal-risk-selection, updating interventionStore', event.detail)
      interventionStore.update(Array.from(event.detail))
    }, 50))
  }

  template() {
    const interventionCount = Object.values(interventionStore.value).flatMap((c) => Array.from(c)).length
    const countHtml = `<p>Total: ${interventionCount}</p>`

    const listItems = Object.entries(interventionStore.value).reduce((html, currentGroup) => {
      const category = currentGroup[0]
      const ids = [...currentGroup[1]]

      if (ids.length) {
        let categoryHtml = `<h3 class="category-heading">
            ${interventionData[category].category_name}
          </h3>`
        const idList = ids.sort((a, b) => a - b).map((id) => {
          return interventionSummaryTemplate(category, id)
        })
        categoryHtml += `<ul class="intervention-list">${idList.join('')}</ul>`

        return html + categoryHtml
      }

      return html
    }, '')
    return countHtml + listItems
  }
}
